/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { assetLicensesBatchesApiUrls } from '@/configs/api-urls/asset-licenses-batches'
import { setOffset } from '@/utils/pagination'
import { setAuthorizationConfig } from '@/utils/services'
import { setUrlWithParams } from '@/utils/url'
import axios from 'axios'
import { handleThrowError } from '@/utils/fetch'

const apiUrlBase = assetLicensesBatchesApiUrls.base

export const registerAssetsLicensesBatch = async (formData, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .post(
      apiUrlBase,
      formData,
      config
    )
    .then(res => res.data)
    .catch(err => { handleThrowError('registerAssetsLicensesBatch', err) })
}

export const registerCSVAssetsLicensesBatch = async (formData, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken, 'multipart/form-data')

  return axios
    .post(
      `${apiUrlBase}/csv`,
      formData,
      config
    )
    .then(res => res.data)
    .catch(err => { handleThrowError('createCSVAssetsLicensesBatch', err) })
}

export const getAssetsLicensesBatches = async ({ searchParams = {} }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const pagination = {
    limit: 10,
    offset: setOffset(searchParams.page, 10)
  }
  const url = setUrlWithParams(`${apiUrlBase}`, { ...pagination, ...searchParams })

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAssetsLicensesBatches', err) })
}

export const getAssetLicensesBatch = async ({
  id
}, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const url = setUrlWithParams(`${apiUrlBase}/${id}`)
  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAssetLicensesBatch', err) })
}

export const getAssetLicensesBatchItemsByBatchId = async ({ id, searchParams = {} }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const pagination = {
    limit: 10,
    offset: setOffset(searchParams.page, 10)
  }
  const url = setUrlWithParams(`${apiUrlBase}/${id}/items`, { ...pagination, ...searchParams })

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('getAssetLicensesBatchItemsByBatchId', err) })
}

export const startAssetLicensesBatchProcessing = async ({ id }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)

  return axios
    .post(`${apiUrlBase}/${id}/licensed-assets`, {}, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('startAssetLicensesBatchProcessing', err) })
}

export const retryAssetLicensesBatch = async ({ batchId }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const url = `${apiUrlBase}/${batchId}/retry-acquire-sam-licenses`

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('retryAssetLicensesBatch', err) })
}

export const retryAssetLicensesBatchItem = async ({ batchId, itemId }, getAccessToken) => {
  const config = await setAuthorizationConfig(getAccessToken)
  const url = `${apiUrlBase}/${batchId}/retry-acquire-sam-licenses/${itemId}`

  return axios
    .get(url, config)
    .then(res => res.data)
    .catch(err => { handleThrowError('retryAssetLicensesBatchItem', err) })
}
